import { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "@/config";
import { Shield, CheckCircle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function AuctionAccountRequestPage() {
    const [accountType, setAccountType] = useState<"BUYER" | "SELLER" | "BOTH">("BUYER");
    const [licenseNo,   setLicenseNo]   = useState("");
    const [existing,    setExisting]    = useState<{ status: string; account_type: string } | null>(null);
    const [submitted,   setSubmitted]   = useState(false);
    const [loading,     setLoading]     = useState(false);
    const [error,       setError]       = useState<string | null>(null);

    useEffect(() => {
        axios.get(`${API_URL}/auction/accounts/mine`, { withCredentials: true })
            .then((r) => { if (r.data.account) setExisting(r.data.account); })
            .catch(() => {});
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            await axios.post(`${API_URL}/auction/accounts/request`, {
                account_type: accountType,
                license_no: licenseNo || undefined,
            }, { withCredentials: true });
            setSubmitted(true);
        } catch (err: any) {
            setError(err?.response?.data?.message ?? "Request failed.");
        } finally {
            setLoading(false);
        }
    };

    if (existing) {
        return (
            <div className="max-w-lg mx-auto py-16 px-4 text-center space-y-4">
                {existing.status === "APPROVED" ? (
                    <>
                        <CheckCircle className="h-14 w-14 text-green-500 mx-auto" />
                        <h2 className="text-2xl font-bold">Account Approved</h2>
                        <p className="text-muted-foreground">
                            Your <strong>{existing.account_type}</strong> account is active. You can bid and list vehicles on the marketplace.
                        </p>
                        <Button onClick={() => window.location.href = "/auctions/marketplace"} className="mt-2">
                            Browse Marketplace
                        </Button>
                    </>
                ) : (
                    <>
                        <Clock className="h-14 w-14 text-orange-400 mx-auto" />
                        <h2 className="text-2xl font-bold">Request Pending</h2>
                        <p className="text-muted-foreground">
                            Your <strong>{existing.account_type}</strong> account request is under review. We'll notify you once approved.
                        </p>
                    </>
                )}
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="max-w-lg mx-auto py-16 px-4 text-center space-y-4">
                <CheckCircle className="h-14 w-14 text-green-500 mx-auto" />
                <h2 className="text-2xl font-bold">Request Submitted</h2>
                <p className="text-muted-foreground">
                    Your auction account request has been submitted and is pending review.
                    You'll be notified once it's approved.
                </p>
            </div>
        );
    }

    return (
        <div className="max-w-lg mx-auto py-8 px-4 space-y-6">
            <div className="text-center">
                <Shield className="h-12 w-12 mx-auto text-indigo-600 mb-3" />
                <h1 className="text-2xl font-bold">Request Auction Account</h1>
                <p className="text-muted-foreground mt-2 text-sm">
                    Apply for a buyer, seller, or combined auction account to participate in the IDSCRM marketplace.
                </p>
            </div>

            <form onSubmit={handleSubmit} className="bg-card rounded-xl border p-6 space-y-5">
                <div>
                    <label className="text-sm font-medium mb-2 block">Account Type</label>
                    <div className="grid grid-cols-3 gap-2">
                        {(["BUYER", "SELLER", "BOTH"] as const).map((t) => (
                            <button
                                key={t}
                                type="button"
                                onClick={() => setAccountType(t)}
                                className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${
                                    accountType === t
                                        ? "bg-indigo-600 text-white border-indigo-600"
                                        : "hover:bg-muted"
                                }`}
                            >
                                {t === "BOTH" ? "Buyer + Seller" : t.charAt(0) + t.slice(1).toLowerCase()}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="text-sm font-medium mb-1 block">Dealer / Auction License # (optional)</label>
                    <Input
                        placeholder="License number if applicable"
                        value={licenseNo}
                        onChange={(e) => setLicenseNo(e.target.value)}
                    />
                </div>

                {error && <p className="text-red-500 text-sm">{error}</p>}

                <Button type="submit" className="w-full bg-indigo-600 text-white hover:bg-indigo-700" disabled={loading}>
                    {loading ? "Submitting…" : "Submit Request"}
                </Button>
            </form>
        </div>
    );
}
